import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "store";

export interface AvailableSeatDetails {
  section: string;
  row: string;
  seat: string;
  ga: boolean;
}

interface TicketBundleState {
  quantity: number;
  selectedSections: string[];
  selectedSeats: AvailableSeatDetails[];
}

const initialState: TicketBundleState = {
  quantity: 1,
  selectedSections: [],
  selectedSeats: [],
};

export const ticketBundleSlice = createSlice({
  name: "ticketBundle",
  initialState,
  reducers: {
    setQuantity: (state, action: PayloadAction<number>) => {
      state.quantity = action.payload;
      // If we have more selected seats than the new quantity, trim the selection
      if (state.selectedSeats.length > action.payload) {
        state.selectedSeats = state.selectedSeats.slice(0, action.payload);
      }
    },
    setSelectedSections: (state, action: PayloadAction<string[]>) => {
      state.selectedSections = action.payload;
      // Clear selected seats when section changes
    },
    addSeat: (state, action: PayloadAction<AvailableSeatDetails>) => {
      const seat = action.payload;

      // Check if this seat is already selected
      const existingIndex = state.selectedSeats.findIndex(
        (s) =>
          s.section === seat.section &&
          s.row === seat.row &&
          s.seat === seat.seat
      );

      // If seat is already selected, remove it
      if (existingIndex >= 0) {
        state.selectedSeats = state.selectedSeats.filter(
          (_, index) => index !== existingIndex
        );
        return;
      }

      // If trying to select more seats than quantity, replace the oldest selection
      if (state.selectedSeats.length >= state.quantity) {
        state.selectedSeats = [...state.selectedSeats.slice(1), seat];
        return;
      }

      // Otherwise add the seat
      state.selectedSeats.push(seat);
    },
    removeSeat: (state, action: PayloadAction<AvailableSeatDetails>) => {
      const { section, row, seat }: AvailableSeatDetails = action.payload;
      state.selectedSeats = state.selectedSeats.filter(
        (s) => !(s.section === section && s.row === row && s.seat === seat)
      );
    },
    clearSeats: (state) => {
      state.selectedSeats = [];
    },
    setSelectedSeats: (
      state,
      action: PayloadAction<AvailableSeatDetails[]>
    ) => {
      state.selectedSeats = action.payload;
    },
    resetTicketBundle: (state) => {
      return initialState;
    },
  },
});

// Export actions
export const {
  setQuantity,
  setSelectedSections,
  addSeat,
  removeSeat,
  clearSeats,
  setSelectedSeats,
  resetTicketBundle,
} = ticketBundleSlice.actions;

// Export selectors
export const selectAreSeatsConsecutive = (state: RootState) => {
  const { selectedSeats } = state.ticketBundle;

  if (selectedSeats.length <= 1) return true;

  // Check if all seats are in the same section and row
  const firstSeat = selectedSeats[0];
  const allSameLocation = selectedSeats.every(
    (seat) => seat.section === firstSeat.section && seat.row === firstSeat.row
  );

  if (!allSameLocation) return false;

  // Sort seats by seat number
  const sortedSeats = [...selectedSeats].sort((a, b) => {
    return parseInt(a.seat, 10) - parseInt(b.seat, 10);
  });

  // Check if seat numbers form a consecutive sequence
  for (let i = 1; i < sortedSeats.length; i++) {
    const prevSeatNum = parseInt(sortedSeats[i - 1].seat, 10);
    const currentSeatNum = parseInt(sortedSeats[i].seat, 10);

    if (currentSeatNum !== prevSeatNum + 1) {
      return false;
    }
  }

  return true;
};

export default ticketBundleSlice.reducer;
